<template>
  <div class="checklist-progress-card">
    <div class="header-section">
      <div>
        <span class="progress-text">Progresso: <strong>{{ completedItems }}/{{ totalItems }}</strong></span>
      </div>
    </div>

    <div class="progress-section">
      <div
        class="custom-progress-bar"
        @mouseleave="hoveredSegment = null"
      >
        <div class="progress-segments">
          <div
            v-if="okItemsPercentage > 0"
            class="progress-segment ok-segment"
            :class="{
              'segment-dimmed': hoveredSegment === 'not-ok' || hoveredSegment === 'pending',
              'segment-highlighted': hoveredSegment === 'ok'
            }"
            :style="{ width: okItemsPercentage + '%' }"
            @mouseenter="hoveredSegment = 'ok'"
          ></div>
          <div
            v-if="notOkItemsPercentage > 0"
            class="progress-segment not-ok-segment"
            :class="{
              'segment-dimmed': hoveredSegment === 'ok' || hoveredSegment === 'pending',
              'segment-highlighted': hoveredSegment === 'not-ok'
            }"
            :style="{ width: notOkItemsPercentage + '%' }"
            @mouseenter="hoveredSegment = 'not-ok'"
          ></div>
          <div
            v-if="pendingItemsPercentage > 0"
            class="progress-segment pending-segment"
            :class="{
              'segment-dimmed': hoveredSegment === 'ok' || hoveredSegment === 'not-ok',
              'segment-highlighted': hoveredSegment === 'pending'
            }"
            :style="{ width: pendingItemsPercentage + '%' }"
            @mouseenter="hoveredSegment = 'pending'"
          ></div>
        </div>

        <v-tooltip
          v-if="okItemsPercentage > 0"
          bottom
          color="rgba(0, 0, 0, 0)"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              class="tooltip-activator ok-activator"
              :style="{
                width: okItemsPercentage + '%',
                left: '0%'
              }"
              v-on="on"
              @mouseenter="hoveredSegment = 'ok'"
              @mouseleave="hoveredSegment = null"
            ></div>
          </template>
          <div class="tooltip-content">
            <div class="tooltip-header">
              <div class="tooltip-color-indicator ok-color"></div>
              <span class="tooltip-title">Conforme</span>
            </div>
            <div class="tooltip-details">
              <div>Aprovados: {{ okItems }}</div>
              <div>% do Total: {{ Math.round((okItems / totalItems) * 100) }}%</div>
              <div>Total Verificado: {{ completedItems }}</div>
              <div>Total de Itens: {{ totalItems }}</div>
            </div>
          </div>
        </v-tooltip>

        <v-tooltip
          v-if="notOkItemsPercentage > 0"
          bottom
          color="rgba(0, 0, 0, 0)"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              class="tooltip-activator not-ok-activator"
              :style="{
                width: notOkItemsPercentage + '%',
                left: okItemsPercentage + '%'
              }"
              v-on="on"
              @mouseenter="hoveredSegment = 'not-ok'"
              @mouseleave="hoveredSegment = null"
            ></div>
          </template>
          <div class="tooltip-content">
            <div class="tooltip-header">
              <div class="tooltip-color-indicator not-ok-color"></div>
              <span class="tooltip-title">Não Conforme</span>
            </div>
            <div class="tooltip-details">
              <div>Com Problema: {{ notOkItems }}</div>
              <div>% do Total: {{ Math.round((notOkItems / totalItems) * 100) }}%</div>
              <div>Total Verificado: {{ completedItems }}</div>
              <div>Total de Itens: {{ totalItems }}</div>
            </div>
          </div>
        </v-tooltip>

        <v-tooltip
          v-if="pendingItemsPercentage > 0"
          bottom
          color="rgba(0, 0, 0, 0)"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              v-bind="attrs"
              class="tooltip-activator pending-activator"
              :style="{
                width: pendingItemsPercentage + '%',
                left: (okItemsPercentage + notOkItemsPercentage) + '%'
              }"
              v-on="on"
              @mouseenter="hoveredSegment = 'pending'"
              @mouseleave="hoveredSegment = null"
            ></div>
          </template>
          <div class="tooltip-content">
            <div class="tooltip-header">
              <div class="tooltip-color-indicator pending-color"></div>
              <span class="tooltip-title">Itens Pendentes</span>
            </div>
            <div class="tooltip-details">
              <div>A Verificar: {{ pendingItems }}</div>
              <div>% do Total: {{ Math.round((pendingItems / totalItems) * 100) }}%</div>
              <div>Total Verificado: {{ completedItems }}</div>
              <div>Total de Itens: {{ totalItems }}</div>
            </div>
          </div>
        </v-tooltip>
      </div>
    </div>

    <div class="status-section">
      <div class="status-item">
        <v-icon
          color="#FFB400"
          size="20"
        >
          {{ icons.mdiAlertOutline }}
        </v-icon>
        <span class="status-text"><strong>{{ pendingItems }}</strong> itens pendentes</span>
      </div>
      <div class="status-indicators">
        <div class="status-indicator">
          <div class="status-dot ok-dot"></div>
          <span class="status-label">Conforme</span>
        </div>
        <div class="status-indicator">
          <div class="status-dot not-ok-dot"></div>
          <span class="status-label">Não conforme</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mdiAlertOutline, mdiCarHatchback } from '@mdi/js'

export default {
  name: 'ChecklistProgressBar',
  props: {
    totalItems: {
      type: Number,
      default: 8,
    },
    completedItems: {
      type: Number,
      default: 0,
    },
    okItems: {
      type: Number,
      default: 0,
    },
    notOkItems: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      icons: {
        mdiCarHatchback,
        mdiAlertOutline,
      },
      hoveredSegment: null,
    }
  },
  computed: {
    pendingItems() {
      return this.totalItems - this.completedItems
    },
    okItemsPercentage() {
      if (this.totalItems <= 0) return 0

      return (this.okItems / this.totalItems) * 100
    },
    notOkItemsPercentage() {
      if (this.totalItems <= 0) return 0

      return (this.notOkItems / this.totalItems) * 100
    },
    pendingItemsPercentage() {
      if (this.totalItems <= 0) return 0

      return (this.pendingItems / this.totalItems) * 100
    },
  },
}
</script>

<style scoped>
.checklist-progress-card {
  border-radius: 16px;
  color: #E7E3FC;
  padding-top: 10px;
  background: transparent;
}

.header-section {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
}

.left-info {
  display: flex;
  align-items: center;
}

.vehicle-text {
  font-weight: 400;
  font-size: 14px;
  color: #E7E3FC;
}

.progress-text {
  font-weight: 400;
  font-size: 14px;
  color: #E7E3FC;
}

.progress-section {
  margin-bottom: 16px;
}

.custom-progress-bar {
  height: 8px;
  background-color: #332E4C;
  border-radius: 32px;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  width: 100%;
}

.progress-segments {
  height: 100%;
  display: flex;
  width: 100%;
}

.progress-segment {
  height: 100%;
  transition: all 0.3s ease-in-out;
  cursor: pointer;
  position: relative;
}

.ok-segment {
  background-color: #56CA00;
}

.not-ok-segment {
  background-color: #FF4757;
}

.pending-segment {
  background-color: #3B355A;
}

.progress-segment:hover {
  transform: scaleY(1.2);
  z-index: 10;
}

.segment-dimmed {
  opacity: 0.5;
  filter: brightness(0.8);
}

.segment-highlighted {
  opacity: 1;
  filter: brightness(1.2);
  box-shadow: 0 0 12px rgba(255, 255, 255, 0.4);
}

.tooltip-activator {
  position: absolute;
  top: 0;
  height: 100%;
  z-index: 20;
  cursor: pointer;
}

.tooltip-content {
  background: rgba(0, 0, 0, 0.9);
  border-radius: 8px;
  padding: 12px;
  color: white;
  font-size: 12px;
  min-width: 150px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.tooltip-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
}

.tooltip-color-indicator {
  width: 12px;
  height: 12px;
  border-radius: 2px;
}

.ok-color {
  background-color: #56CA00;
}

.not-ok-color {
  background-color: #FF4757;
}

.pending-color {
  background-color: #3B355A;
}

.tooltip-title {
  font-weight: 600;
  font-size: 13px;
}

.tooltip-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tooltip-details > div {
  display: flex;
  justify-content: space-between;
  opacity: 0.9;
}

@keyframes pulse {
  0%, 100% {
    transform: scaleY(1);
  }
  50% {
    transform: scaleY(1.15);
  }
}

.progress-segment:hover {
  animation: pulse 2s ease-in-out infinite;
}

.status-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.status-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-text {
  font-size: 14px;
  color: #FFB400;
  font-weight: 400;
}

.status-indicators {
  display: flex;
  gap: 20px;
}

.status-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 3px;
}

.ok-dot {
  background-color: #56CA00;
}

.not-ok-dot {
  background-color: #FF4757;
}

.status-label {
  font-size: 12px;
  color: #E7E3FC;
  font-weight: 400;
}
</style>
